/**
 * The one place that knows how a CardId becomes a file.
 *
 * Pip cards, aces and the joker are `.svg`; the three courts were rasterised
 * to `.webp` (assets/cards/NOTICE.md: 33 MB → 1.9 MB), because only they carry
 * vector art too detailed to be worth its weight at this size, and so was the
 * back, which is drawn thirty times on a table and was the costliest thing in
 * its first frame (docs/PERFORMANCE.md). That
 * conditional exists here and nowhere else — scattering it is how a deck switch
 * or a re-render breaks a single rank across three components.
 */
import { faceOf, RANK_LABEL, SUIT_LABEL, type CardId, type Rank, type Suit } from '@cards/shared'

export type DeckName = 'fourcolor' | 'twocolor'

/** The shipped deck. Two colour is what a player has held in their hands every
 *  time they have held a pack, and a four-colour deck reads as a novelty to
 *  anyone who has not asked for one — so it is the setting, not the default.
 *  Every `deck` parameter below defaults here rather than to a literal, so the
 *  choice is made in one place. */
export const DEFAULT_DECK: DeckName = 'twocolor'

/**
 * Every face of both decks, as the URL the build gave it.
 *
 * Imported rather than served by name from a public directory, so each file
 * is published under a content hash (`/assets/heart7-<hash>.svg`) and can be
 * cached as immutable. Served by name, every visit had to ask about every
 * card again — fifty-odd revalidations, one round trip each on a phone's
 * connection, before the deck could be called loaded. Only URLs are imported:
 * nothing is fetched until a card is drawn or the deck is preloaded.
 */
const FILES: Record<string, string> = import.meta.glob('../../../assets/cards/*/*.{svg,webp}', {
  eager: true,
  query: '?url',
  import: 'default',
})
const PREFIX = '../../../assets/cards/'

function file(deck: DeckName, name: string): string {
  // A name that is not in the glob is a bug in this file, and a broken image
  // says so on screen; there is no other place the face could be found.
  return FILES[`${PREFIX}${deck}/${name}`] ?? ''
}

const SUIT_DIR: Record<Suit, string> = { S: 'spade', H: 'heart', D: 'diamond', C: 'club' }
const COURT_FILE: Partial<Record<Rank, string>> = { 11: 'Jack', 12: 'Queen', 13: 'King', 14: 'Ace' }
/** Only the three courts were rasterised; the ace stayed vector. */
const RASTER: ReadonlySet<Rank> = new Set<Rank>([11, 12, 13])

const SUIT_WORD: Record<Suit, string> = { S: 'spades', H: 'hearts', D: 'diamonds', C: 'clubs' }
const RANK_WORD: Partial<Record<Rank, string>> = { 11: 'jack', 12: 'queen', 13: 'king', 14: 'ace' }

export const SUIT_GLYPH: Record<Suit, string> = SUIT_LABEL
export const SUIT_CLASS: Record<Suit, string> = { S: 's-s', H: 's-h', D: 's-d', C: 's-c' }

/** `BACK` is not a CardId — it is the face-down rendering of any card. */
export type CardArt = CardId | 'BACK'

export function cardSrc(card: CardArt, deck: DeckName = DEFAULT_DECK): string {
  if (card === 'BACK') return file(deck, 'blueBack.webp')
  if (card === 'JOKER') return file(deck, 'redJoker.svg')
  const { rank, suit } = faceOf(card)
  if (rank === null || suit === null) return file(deck, 'blueBack.webp')
  const name = COURT_FILE[rank] ?? String(rank)
  return file(deck, `${SUIT_DIR[suit]}${name}.${RASTER.has(rank) ? 'webp' : 'svg'}`)
}

export function cardAlt(card: CardArt): string {
  if (card === 'BACK') return 'face-down card'
  if (card === 'JOKER') return 'Joker'
  const { rank, suit } = faceOf(card)
  if (rank === null || suit === null) return 'card'
  return `${RANK_WORD[rank] ?? rank} of ${SUIT_WORD[suit]}`
}

/** Short label for text on the felt, e.g. `9♥`. */
export function cardText(card: CardId): string {
  if (card === 'JOKER') return 'Joker'
  const { rank, suit } = faceOf(card)
  return `${RANK_LABEL[rank as Rank]}${SUIT_LABEL[suit as Suit]}`
}
